import React, { Component } from "react";
import { Locked } from "@carbon/icons-react";
import colorHelper from "../../utils/color-helper";
import Slider from "../common/Slider";
import PropTypes from "prop-types";
import FixedSteps from "./FixedSteps";
import uniqueId from "lodash.uniqueid";
import { v4 as uuidv4 } from "uuid";

const lightnessSteps = [97, 90, 82, 72, 59.04, 47.5, 37, 28, 20, 13];
// const lightnessSteps = [97, 90, 82, 72, 61.5, 47.5, 37, 28, 20, 13];
// const lightnessSteps = [13, 20, 28, 37, 47.5, 61.5, 72, 82, 90, 97];

// This actually refers to the 5 step, since arrays start at 0
const defaultStep = 4;
const light = "#ffffff";
const dark = "#000000";

/**
 * Finds the nearest value in an array of numbers.
 * Example: nearestValue(array, 42)
 *
 * @param {Array<number>} arr
 * @param {number} val the ideal value for which the nearest or equal should be found
 */

const nearestValue = (arr, target) =>
  arr.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
  );

/**
 * This component provides a form for adding or editing a color step in the palette.
 * It allows users to input a hex value, adjust the hue, saturation, and lightness values using sliders, and lock specific lightness steps to create custom palettes.
 * The form also provides a preview of the color and its contrast ratios against light and dark backgrounds.
 */
class ColorForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hue: 220.6,
      saturation: 100,
      lightness: lightnessSteps[defaultStep],
      hex: "",
      name: "",
      id: "",
      colors: [],
      lockedColors: [],
    };
  }

  /**
   * When the component mounts, it checks if there is a step prop passed in.
   * If there is, it calls the changeStep function to populate the form with the existing step data.
   * If there is no step prop, it calls the createDefaultState function to initialize the form with default values.
   */
  componentDidMount() {
    Object.keys(this.props.step).length > 0
      ? this.changeStep(this.props.step)
      : this.createDefaultState();
  }

  /**
   *
   * When the component updates, it checks if the step prop has changed by comparing the current step id with the previous step id.
   * If the step id has changed, it calls the changeStep function to update the form with the new step data.
   * If there is no step prop, it calls the createDefaultState function to reset the form to default values.
   * @param {*} prevProps
   * @param {*} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.step &&
      prevProps.step &&
      this.props.step.id !== prevProps.step.id
    ) {
      this.props.step.id ? this.changeStep() : this.createDefaultState();
    }
  }

  /**
   *
   * This function validates the hex value input by the user.
   * It converts the hex value to HSLuv color space and finds the closest lightness step to the input color.
   * It then calculates the contrast ratios of both the input color and the closest color against light and dark backgrounds, and updates the state with this information to display it in the form.
   * @param {*} e
   * @returns
   */
  validateHex(e) {
    e.preventDefault();
    const testHex = e.target.colorHex.value;
    const converterHSLUV = colorHelper.converter("hsluv");
    const converterHex = colorHelper.converter("hex");
    const closestHSL = converterHSLUV(testHex);
    const testHSL = converterHSLUV(testHex);
    if (closestHSL === undefined) {
      return;
    }

    closestHSL.l = nearestValue(lightnessSteps, closestHSL.l);
    const closestHex = converterHex(closestHSL);
    const testColors = {
      closestHex: closestHex,
      closestHSL: closestHSL,
      testHex: testHex,
      testHSL: testHSL,
      testB: parseFloat(colorHelper.wcagContrast(testHex, dark).toFixed(2)),
      testW: parseFloat(colorHelper.wcagContrast(testHex, light).toFixed(2)),
      closestB: parseFloat(
        colorHelper.wcagContrast(closestHex, dark).toFixed(2),
      ),
      closestW: parseFloat(
        colorHelper.wcagContrast(closestHex, light).toFixed(2),
      ),
    };

    this.setState({ testColors: testColors });
  }

  /**
   * This function creates the default state for the form when there is no existing step data.
   * It initializes the hue, saturation, lightness, hex value, name, id, colors, and lockedColors state variables based on the default hue, saturation, and lightness values.
   * The hex value is calculated from the HSLuv color using a color helper function.
   * The colors array is populated with the default lightness steps based on the current hue and saturation values.
   */
  createDefaultState() {
    const hslColor = {
      mode: "hsluv",
      h: this.state.hue,
      s: this.state.saturation,
      l: this.state.lightness,
    };

    this.setState({
      hue: hslColor.h,
      saturation: hslColor.s,
      lightness: hslColor.l,
      hex: colorHelper.formatter("hex")(hslColor),
      name: "New Color",
      id: uuidv4(),
      colors: this.createSteps(),
      lockedColors: [],
    });
  }

  /**
   *
   * This function is called when the user submits the form to add a new color step.
   * It creates a new step object with the current state values for hue, saturation, lightness, hex, name, id, colors, and lockedColors.
   * It then calls the onAddStep function passed in as a prop to add the new step to the palette.
   * @param {*} e
   */
  onAddStep(e) {
    e.preventDefault();
    const newStep = {
      name: this.state.name || uniqueId("color_"),
      id: uuidv4(),
      colors: this.state.colors,
      lockedColors: this.state.lockedColors,
      hue: this.state.hue,
      saturation: this.state.saturation,
      lightness: this.state.lightness,
      hex: this.state.hex,
    };
    this.props.onAddStep(newStep);
  }

  /**
   *
   * This function is called when the user submits the form to edit an existing color step.
   * @param {*} e
   */
  onEditStep(e) {
    e.preventDefault();

    const updatedStep = {
      name: this.state.name || uniqueId("color_"),
      id: this.state.id || uuidv4(),
      colors: this.state.colors,
      lockedColors: this.state.lockedColors,
      hue: this.state.hue,
      saturation: this.state.saturation,
      lightness: this.state.lightness,
      hex: this.state.hex,
    };
    this.props.onEditStep(updatedStep);
  }

  /**
   *
   * This function creates a lightness gradient based on the current hue and saturation values in the state.
   * @returns
   */
  createLightnessGradient() {
    const hslColor = {
      mode: "hsluv",
      h: this.state.hue,
      s: this.state.saturation,
      l: this.state.lightness,
    };
    const gradientValues = [];

    for (let i = 0; i < 21; i++) {
      hslColor.l = 5 * i;
      gradientValues.push(colorHelper.formatter("hex")(hslColor));
    }

    return gradientValues;
  }

  /**
   * This function handles changes to the color name input field.
   * @param {*} e
   */
  handleNameChange(e) {
    this.setState({ name: e.target.value });
  }

  /**
   *
   * This function locks or unlocks a specific lightness step when the user checks or unchecks the corresponding checkbox in the form.
   * This prevents the color from being updated when the hue or saturation sliders are adjusted, allowing users to create custom palettes with specific lightness values.
   * @param {*} e
   */
  lockColor(e) {
    let lockedColors = [...this.state.lockedColors];

    if (e.target.checked) {
      lockedColors.push(parseInt(e.target.value));
    } else {
      lockedColors = lockedColors.filter((colorIndex) => {
        return colorIndex !== parseInt(e.target.value);
      });
    }

    this.setState({ lockedColors: lockedColors });
  }

  /**
   *
   * This function creates the color steps for the palette based on the current hue and saturation values in the state, while keeping any locked lightness steps unchanged.
   * @returns
   */
  createSteps() {
    const steps = [...this.state.colors];
    for (let i = 0; i < lightnessSteps.length; i++) {
      if (this.state.lockedColors.find((lock) => lock === i) === undefined) {
        const hslColor = {
          mode: "hsluv",
          h: this.state.hue,
          s: this.state.saturation,
          l: lightnessSteps[i],
        };
        steps[i] = hslColor;
      }
    }

    return steps;
  }

  /**
   * This function creates a gradient for the lightness steps based on the current hue and saturation values in the state.
   * @returns
   */
  createFixedGradient() {
    const steps = this.state.colors;
    const gradientValues = [];

    for (let i = steps.length - 1; i > -1; i--) {
      let percent = 0;

      if (i === steps.length - 1 || i === 0) {
        percent = (steps[i].l / 100) * 100 + "%";
      } else {
        percent =
          (steps[i].l / 100) * 100 + "% " + (steps[i - 1].l / 100) * 100 + "%";
      }
      gradientValues.push(
        colorHelper.formatter("hex")(steps[i]) + " " + percent,
      );
    }

    return gradientValues;
  }

  /**
   *
   * This function creates a hue gradient based on the current saturation and lightness values in the state.
   * This gradient is used as the background for the hue slider to provide a visual representation of how the hue value will affect the colors in the palette.
   * @returns
   */
  createHueGradient() {
    const hslColor = {
      mode: "hsluv",
      h: this.state.hue,
      s: this.state.saturation,
      l: this.state.lightness,
    };
    const gradientValues = [];

    for (let i = 0; i < 21; i++) {
      hslColor.h = 18 * i;
      gradientValues.push(colorHelper.formatter("hex")(hslColor));
    }

    return gradientValues;
  }

  /**
   *
   * This function creates a saturation gradient based on the current hue and lightness values in the state.
   * This gradient is used as the background for the saturation slider to provide a visual representation of how the saturation value will affect the colors in the palette.
   * @returns
   */
  createSaturationGradient() {
    const hslColor = {
      mode: "hsluv",
      h: this.state.hue,
      s: this.state.saturation,
      l: this.state.lightness,
    };
    const gradientValues = [];

    for (let i = 0; i < 21; i++) {
      hslColor.s = 5 * i;
      gradientValues.push(colorHelper.formatter("hex")(hslColor));
    }

    return gradientValues;
  }

  /**
   *
   * This function is called when the user clicks the "use color" button in the form after validating a hex value.
   * This value is used to create the color steps.
   * @param {*} hex
   */
  useColor(hex) {
    const converterHSLUV = colorHelper.converter("hsluv");
    const hexParse = converterHSLUV(hex);

    this.setState({ hue: parseFloat(hexParse.h) }, this.updateSample);
    this.setState({ saturation: parseFloat(hexParse.s) }, this.updateSample);
  }

  /**
   *
   * This function is called to populate the form with existing step data when editing a color step.
   * @param {*} step
   */
  changeStep(step) {
    this.setState({ ...this.props.step });
  }

  /**
   * This function is called to update the color sample and the color steps in the form whenever the hue or saturation values are changed.
   */
  updateSample() {
    const hslColor = {
      mode: "hsluv",
      h: this.state.hue,
      s: this.state.saturation,
      l: this.state.lightness,
    };

    this.setState({
      hex: colorHelper.formatter("hex")(hslColor),
      colors: this.createSteps(),
    });
  }

  /**
   *
   * This function is called when the user adjusts the hue slider in the form. It updates the hue value in the state and then calls the updateSample function to update the color sample and the color steps based on the new hue value.
   * @param {*} data
   */
  onHueChange(data) {
    this.setState({ hue: data }, this.updateSample);
  }

  /**
   * This function is called when the user adjusts the saturation slider in the form. It updates the saturation value in the state and then calls the updateSample function to update the color sample and the color steps based on the new saturation value.
   * @param {*} data
   */
  onSaturationChange(data) {
    this.setState({ saturation: data }, this.updateSample);
  }

  /**
   * This function is called when the user adjusts the lightness slider in the form. It finds the nearest valid lightness value from the predefined lightness steps and updates the lightness value in the state, then calls the updateSample function to update the color sample and the color steps based on the new lightness value.
   * Not currently available as a slider in the form, but could be added in the future if users want more control over the lightness values.
   * @param {*} data
   */
  onLightnessChange(data) {
    const validValue = nearestValue(lightnessSteps, data.value);
    this.setState({ lightness: validValue }, this.updateSample);
  }

  /**
   *
   * Renders the form for adding or editing a color step, including input fields for the color name and hex value, sliders for adjusting the hue and saturation values, and a preview of the color with its contrast ratios against light and dark backgrounds. The form also includes checkboxes for locking specific lightness steps to create custom palettes.
   * @returns
   */
  render() {
    const style = {
      backgroundColor: this.state.hex,
    };
    const lightnessTrack = {
      background:
        "linear-gradient(to right, " +
        this.createFixedGradient().join(",") +
        ")",
    };
    const saturationTrack =
      "linear-gradient(to right, " +
      this.createSaturationGradient().join(",") +
      ")";
    const hueTrack =
      "linear-gradient(to right, " + this.createHueGradient().join(",") + ")";

    return (
      <div className="color-form">
        <div className="add-color--container">
          <div className="row">
            <div className="col-12">
              <h2>Use Hex Value</h2>
              <form
                onSubmit={(e) => {
                  this.validateHex(e);
                }}
              >
                <div className="row">
                  <div className="col-12 mb-3">
                    If you want to start with a specific hex value, enter it
                    here. The closest color is determined by finding the color
                    with the closest lightness value to the input hex color.
                  </div>
                  <div className="col-4">
                    <label htmlFor="addColorhex">Hex Value to validate</label>
                    <input
                      id="addColorhex"
                      name="colorHex"
                      type="text"
                      placeholder="#000000"
                      defaultValue="#007b00"
                    />
                  </div>
                  <div className="col-4 button-group">
                    <button type="submit">Check Color</button>
                  </div>
                </div>
                <div className="row color-check-example">
                  {this.state.testColors && (
                    <div className="col-12">
                      <h3>Closest Color in Lightness</h3>
                    </div>
                  )}
                  {this.state.testColors && (
                    <div
                      style={{
                        backgroundColor: this.state.testColors.testHex,
                        color:
                          this.state.testColors.testB >
                          this.state.testColors.testW
                            ? dark
                            : light,
                      }}
                      className="col-6 color-sample"
                    >
                      <div>{this.state.testColors.testHex}</div>
                      <div className="color_step-contrast--dark">
                        <span aria-label="contrast ratio on black">B:</span>{" "}
                        {this.state.testColors.testB}
                      </div>
                      <div className="color_step-contrast--light">
                        <span aria-label="contrast ratio on white">W:</span>{" "}
                        {this.state.testColors.testW}
                      </div>
                    </div>
                  )}
                  {this.state.testColors && (
                    <div
                      style={{
                        backgroundColor: this.state.testColors.closestHex,
                        color:
                          this.state.testColors.closestB >
                          this.state.testColors.closestW
                            ? dark
                            : light,
                      }}
                      className="col-6 color-sample"
                    >
                      <div>Closest: {this.state.testColors.closestHex}</div>
                      <div className="color_step-contrast--dark">
                        <span aria-label="contrast ratio on black">B:</span>{" "}
                        {this.state.testColors.closestB}
                      </div>
                      <div className="color_step-contrast--light">
                        <span aria-label="contrast ratio on white">W:</span>{" "}
                        {this.state.testColors.closestW}
                      </div>
                      <button
                        type="button"
                        size="xs"
                        onClick={(e) => {
                          this.useColor(this.state.testColors.closestHSL);
                        }}
                      >
                        use color
                      </button>
                    </div>
                  )}
                </div>
              </form>
              <hr />
            </div>
            <div className="col-12">
              <div className="color-form--header">
                {this.props.type === "add" && <h2>Add Color Step</h2>}
                {this.props.type === "edit" && <h2>Update Color Step</h2>}
                <button
                  onClick={(e) => this.props.onCancel(e)}
                  className="undo-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <div className="row ">
            <div className="col-8">
              <label htmlFor="addColorName">Color Name</label>
              <input
                id="addColorName"
                name="colorName"
                type="text"
                placeholder="Name this color"
                value={this.state.name}
                onChange={(e) => {
                  this.handleNameChange(e);
                }}
              />
              <div className="color-step">
                <Slider
                  ariaLabelInput="Hue"
                  id="hueSlider"
                  labelText="Hue"
                  max={360}
                  min={0}
                  step={0.01}
                  value={this.state.hue}
                  onChange={(e) => this.onHueChange(e.target.value)}
                  background={hueTrack}
                />
              </div>
              <div className="color-step">
                <Slider
                  ariaLabelInput="Saturation"
                  id="saturationSlider"
                  labelText="Saturation"
                  max={100}
                  min={0}
                  step={0.01}
                  value={this.state.saturation}
                  onChange={(e) => this.onSaturationChange(e.target.value)}
                  background={saturationTrack}
                />
              </div>
              <div>
                <FixedSteps
                  trackStyle={lightnessTrack}
                  steps={lightnessSteps}
                  maxValue={100}
                  defaultStep={defaultStep}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="add-color-sample color-step" style={style}>
                <span>60</span>
                {this.props.type === "add" && (
                  <button
                    className="add-color-sample--button"
                    onClick={(e) => this.onAddStep(e)}
                    size="xs"
                    type="submit"
                  >
                    Save Step
                  </button>
                )}
                {this.props.type === "edit" && (
                  <button
                    className="add-color-sample--button"
                    onClick={(e) => this.onEditStep(e)}
                    size="xs"
                    type="submit"
                  >
                    Update Step
                  </button>
                )}
              </div>
            </div>
            <div className="col-12">
              <ul className="color-step--list--sample">
                {this.state.colors.map((step, index) => {
                  return (
                    <li
                      key={index}
                      style={{
                        color:
                          colorHelper.wcagContrast(step, dark) <
                          colorHelper.wcagContrast(step, light)
                            ? "#ffffff"
                            : "#000000",
                        backgroundColor: colorHelper.formatter("hex")(step),
                      }}
                    >
                      <label className="color-form--step-color">
                        <input
                          type="checkbox"
                          name="lockColor"
                          value={index}
                          onChange={(e) => this.lockColor(e)}
                          checked={
                            !(
                              this.state.lockedColors.find(
                                (lock) => lock === index,
                              ) === undefined
                            )
                          }
                        />
                        <div className="color-form--step-color-example">
                          <div className="color-step--id">
                            {index * 10 + 10}
                          </div>
                          <div className="color-step--hex">
                            {/* {colorHelper.formatter("hex")(step)} */}
                          </div>
                          <div className="color-step--locked">
                            <Locked />
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <ul>
                <li>
                  Note: Lightness values are fixed to 10 steps. This allows for
                  consistent contrast ratios between steps and predictable color
                  values.
                </li>
                <li>
                  To create custom steps with different Hues and Saturations,
                  lock the colors you want to keep by clicking on the color
                  step.{" "}
                </li>
                <li>
                  Locked colors will be kept the same, while unlocked colors
                  will update based on the current Hue and Saturation
                  values.{" "}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ColorForm.propTypes = {
  type: PropTypes.oneOf(["add", "edit"]).isRequired,
  onUpdateStep: PropTypes.func,
  onEditStep: PropTypes.func,
  onAddStep: PropTypes.func,
  step: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    colors: PropTypes.array,
  }),
  lockedColors: PropTypes.array,
};

ColorForm.defaultProps = {
  onAddStep: () => {},
  step: undefined,
};

export default ColorForm;
